import React, { lazy, Suspense, useMemo } from "react";
import { createBrowserRouter, Outlet, RouterProvider, useLocation } from "react-router-dom";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import AuthHandler from "./components/shared/AuthHandler";
import ChangelogHandler from "./components/shared/ChangelogHandler";
import Footer from "./components/shared/Footer";
import BackgroundWrapper from "./components/shared/BackgroundWrapper";
import ErrorBoundary from "./components/shared/ErrorBoundary";
import Header from "./components/shared/Header";
import LoadingScreen from "./components/shared/LoadingScreen";
import ProtectedIsAdminRoute from "./components/shared/ProtectedIsAdminRoute";
import ProtectedIsAuthenticatedRoute from "./components/shared/ProtectedIsAuthenticatedRoute";
import ProtectedIsConfiguredRoute from "./components/shared/ProtectedIsConfiguredRoute";
import ProtectedIsSubscribedRoute from "./components/shared/ProtectedIsSubscribedRoute";
import PremiumPreviewBanner from "./components/shared/PremiumPreviewBanner";
import RealmLimitExceededPopup from "./components/shared/RealmLimitExceededPopup";
import RouterErrorFallback from "./components/shared/RouterErrorFallback";
import UpdateAvailableBanner from "./components/shared/UpdateAvailableBanner";
import { TourProvider } from "./tours/TourProvider";
import { isClassicActive } from "./helpers/helpers";
import { useRealmLimitPopup } from "./hooks/useRealmLimitPopup";
import { useSubscriptionSync } from "./hooks/useSubscriptionSync";
import { useVersionCheck } from "./hooks/useVersionCheck";
import BackToTop from "./pages/shared/BackToTop";
import Home from "./pages/shared/Home";
import UserLookup from "./pages/shared/UserLookup";
import { useAuth } from "./zustand/Zustand";
import { Navigate } from "react-router-dom";

// Lazy load all page components
const Register = lazy(() => import("./pages/shared/Register"));
const ConfirmEmail = lazy(() => import("./pages/shared/ConfirmEmail"));
const Login = lazy(() => import("./pages/shared/Login"));
const ForgotPassword = lazy(() => import("./pages/shared/ForgotPassword"));
const ResetPassword = lazy(() => import("./pages/shared/ResetPassword"));
const ContactUs = lazy(() => import("./pages/shared/ContactUs"));
const RefundPolicy = lazy(() => import("./pages/shared/RefundPolicy"));
const TermsAndConditions = lazy(() => import("./pages/shared/TermsAndConditions"));
const PrivacyPolicy = lazy(() => import("./pages/shared/PrivacyPolicy"));
const AffiliatePage = lazy(() => import("./pages/shared/AffiliatePage"));
const DiscordCallbackPage = lazy(() => import("./pages/discord/callback"));
const AcceptTerms = lazy(() => import("./pages/shared/AcceptTerms"));
const Subscription = lazy(() => import("./pages/shared/Subscription"));
const PaymentSuccess = lazy(() => import("./pages/shared/PaymentSuccess"));
const StatsTable = lazy(() => import("./pages/shared/StatsTable"));
const AddSubscriptionTime = lazy(() => import("./pages/shared/AddSubscriptionTime"));
const ItemLookup = lazy(() => import("./pages/shared/ItemLookup"));
const SnipeAudit = lazy(() => import("./pages/shared/SnipeAudit"));
const EmailPreviews = lazy(() => import("./pages/shared/EmailPreviews"));

// Retail pages (lazy loaded)
const RetailToolRealmConfig = lazy(() => import("./pages/retail/RetailToolRealmConfig"));
const RetailToolPersonalConfig = lazy(() => import("./pages/retail/RetailToolPersonalConfig"));
const RetailCrossRealmFlippingTool = lazy(() => import("./pages/retail/RetailCrossRealmFlippingTool"));
const RetailCrossRealmHousingFlippingTool = lazy(() => import("./pages/retail/RetailCrossRealmHousingFlippingTool"));
const RetailCrossRealmDropshipTool = lazy(() => import("./pages/retail/RetailCrossRealmDropshipTool"));
const InventoryAnalysisTool = lazy(() => import("./pages/retail/InventoryAnalysisTool"));
const RetailRealmStats = lazy(() => import("./pages/retail/RetailRealmStats"));
const ClassicDealsTool = lazy(() => import("./pages/classic/ClassicDealsTool"));
const RetailTSMListGenerator = lazy(() => import("./pages/retail/RetailTSMListGenerator"));
const TSMProfile = lazy(() => import("./pages/retail/TSMProfile"));
const RetailToolDiscordConfig = lazy(() => import("./pages/retail/RetailToolDiscordConfig"));
const UserAccountConfig = lazy(() => import("./pages/shared/UserAccountConfig"));
const SnipeAlertsPage = lazy(() => import("./pages/retail/SnipeAlertsPage"));
const Dashboard = lazy(() => import("./pages/shared/Dashboard"));

// Layout component
const Layout: React.FC = () => {
  const { userData } = useAuth();
  const location = useLocation();

  useSubscriptionSync();

  const subscriptionRoutes = useMemo(() => [
    '/tools/',
    '/guides/',
    '/settings/',
    '/dashboard',
  ], []);

  const excludedRoutes = useMemo(() => ['/settings/realm', '/accept-terms', '/subscription', '/account'], []);

  const isSubscriptionRoute = useMemo(() =>
    subscriptionRoutes.some(route => location.pathname.startsWith(route)),
    [subscriptionRoutes, location.pathname]
  );

  const isExcludedRoute = useMemo(() =>
    excludedRoutes.some(route => location.pathname.startsWith(route)),
    [excludedRoutes, location.pathname]
  );

  const realmLimitExceeded = useRealmLimitPopup({ userData });
  const { updateAvailable } = useVersionCheck();

  const shouldShowPopup = useMemo(() =>
    realmLimitExceeded && isSubscriptionRoute && !isExcludedRoute,
    [realmLimitExceeded, isSubscriptionRoute, isExcludedRoute]
  );

  return (
    <ErrorBoundary>
      <TourProvider>
        {/* Wrapper is locked to 100vh; main scrolls internally when content is
            taller (long marketing page). Tool pages set `h-full overflow-hidden`
            so their viewport sits exactly between header and footer with no scroll. */}
        <div className="flex flex-col h-screen">
          <Header />
          <AuthHandler />
          <ChangelogHandler />
          <main className="flex-1 min-h-0 overflow-y-auto">
            <Suspense fallback={<div className="flex items-center justify-center h-full"><LoadingScreen contained /></div>}>
              <Outlet />
            </Suspense>
          </main>
          <Footer showCta={location.pathname === '/'} />
        </div>
        <BackToTop />
        <RealmLimitExceededPopup isOpen={shouldShowPopup} />
        {updateAvailable && (
          <UpdateAvailableBanner onRefresh={() => window.location.reload()} />
        )}
        <PremiumPreviewBanner />
      </TourProvider>
    </ErrorBoundary>
  );
};

// Helper component for double-protected routes
const ProtectedSubscribedRoute: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  return (
    <ProtectedIsAuthenticatedRoute>
      <ProtectedIsSubscribedRoute>{children}</ProtectedIsSubscribedRoute>
    </ProtectedIsAuthenticatedRoute>
  );
};

// Helper component for routes that also require realm config to be set up
const ProtectedConfiguredRoute: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  return (
    <ProtectedSubscribedRoute>
      <ProtectedIsConfiguredRoute>{children}</ProtectedIsConfiguredRoute>
    </ProtectedSubscribedRoute>
  );
};

// Like <Navigate replace> but carries the current query string to the destination,
// so deeplinks like /snipe-alerts?itemID=4434 still reach the new page with params.
const NavigatePreserveSearch: React.FC<{ to: string }> = ({ to }) => {
  const location = useLocation();
  return <Navigate to={{ pathname: to, search: location.search }} replace />;
};

// Router configuration
const router = createBrowserRouter([
  {
    path: "/",
    element: <Layout />,
    errorElement: <RouterErrorFallback />,
    children: [
      // Public routes
      { index: true, element: <Home /> },
      { path: "register", element: <Register /> },
      { path: "confirm-email/:confirmationToken", element: <ConfirmEmail /> },
      { path: "login", element: <Login /> },
      { path: "forgot-password", element: <ForgotPassword /> },
      { path: "reset-password/:resetToken", element: <ResetPassword /> },
      { path: "contact-us", element: <ContactUs /> },
      { path: "refund-policy", element: <RefundPolicy /> },
      { path: "terms-and-conditions", element: <TermsAndConditions /> },
      { path: "privacy-policy", element: <PrivacyPolicy /> },
      { path: "affiliate", element: <AffiliatePage /> },
      { path: "auth/discord/callback", element: <DiscordCallbackPage /> },

      // Authenticated routes
      {
        path: "accept-terms",
        element: (
          <ProtectedIsAuthenticatedRoute>
            <AcceptTerms />
          </ProtectedIsAuthenticatedRoute>
        ),
      },
      {
        path: "subscription",
        element: (
          <ProtectedIsAuthenticatedRoute>
            <Subscription />
          </ProtectedIsAuthenticatedRoute>
        ),
      },
      {
        path: "payment-success",
        element: (
          <ProtectedIsAuthenticatedRoute>
            <PaymentSuccess />
          </ProtectedIsAuthenticatedRoute>
        ),
      },
      {
        path: "admin/stats",
        element: (
          <ProtectedIsAdminRoute>
            <StatsTable />
          </ProtectedIsAdminRoute>
        ),
      },
      {
        path: "admin/gold",
        element: (
          <ProtectedIsAdminRoute>
            <AddSubscriptionTime />
          </ProtectedIsAdminRoute>
        ),
      },
      {
        path: "admin/user",
        element: (
          <ProtectedIsAdminRoute>
            <UserLookup />
          </ProtectedIsAdminRoute>
        ),
      },
      {
        path: "admin/item",
        element: (
          <ProtectedIsAdminRoute>
            <ItemLookup />
          </ProtectedIsAdminRoute>
        ),
      },
      {
        path: "admin/snipe-audit",
        element: (
          <ProtectedIsAdminRoute>
            <SnipeAudit />
          </ProtectedIsAdminRoute>
        ),
      },
      {
        path: "admin/emails",
        element: (
          <ProtectedIsAdminRoute>
            <EmailPreviews />
          </ProtectedIsAdminRoute>
        ),
      },

      // Dashboard
      {
        path: "dashboard",
        element: (
          <ProtectedConfiguredRoute>
            <Dashboard />
          </ProtectedConfiguredRoute>
        ),
      },

      // Tool routes
      {
        path: "tools/flipping",
        element: (
          <ProtectedConfiguredRoute>
            <RetailCrossRealmFlippingTool />
          </ProtectedConfiguredRoute>
        ),
      },
      {
        path: "tools/housing",
        element: (
          <ProtectedConfiguredRoute>
            <RetailCrossRealmHousingFlippingTool />
          </ProtectedConfiguredRoute>
        ),
      },
      {
        path: "tools/dropship",
        element: (
          <ProtectedConfiguredRoute>
            <RetailCrossRealmDropshipTool />
          </ProtectedConfiguredRoute>
        ),
      },
      {
        path: "tools/inventory",
        element: (
          <ProtectedConfiguredRoute>
            <InventoryAnalysisTool />
          </ProtectedConfiguredRoute>
        ),
      },
      {
        path: "tools/stats",
        element: (
          <ProtectedConfiguredRoute>
            <RetailRealmStats />
          </ProtectedConfiguredRoute>
        ),
      },
      // Classic same-realm undercut deals, behind the VITE_CLASSIC_ACTIVE
      // feature flag. Uses ProtectedSubscribedRoute (not Configured) because
      // classic realm selection happens on the tool itself, not via the retail
      // buy/sell realm config. When the flag is off the route is not registered
      // and the path falls through to the catch-all.
      ...(isClassicActive()
        ? [
            {
              path: "tools/classic-deals",
              element: (
                <ProtectedSubscribedRoute>
                  <ClassicDealsTool />
                </ProtectedSubscribedRoute>
              ),
            },
          ]
        : []),
      {
        path: "tools/tsm",
        element: (
          <ProtectedConfiguredRoute>
            <RetailTSMListGenerator />
          </ProtectedConfiguredRoute>
        ),
      },
      {
        path: "guides/tsm-profile",
        element: (
          <ProtectedConfiguredRoute>
            <TSMProfile />
          </ProtectedConfiguredRoute>
        ),
      },
      {
        path: "tools/snipe",
        element: (
          <ProtectedConfiguredRoute>
            <SnipeAlertsPage />
          </ProtectedConfiguredRoute>
        ),
      },
      {
        path: "settings/realm",
        element: (
          <ProtectedSubscribedRoute>
            <RetailToolRealmConfig />
          </ProtectedSubscribedRoute>
        ),
      },
      {
        path: "settings/personal",
        element: (
          <ProtectedConfiguredRoute>
            <RetailToolPersonalConfig />
          </ProtectedConfiguredRoute>
        ),
      },
      {
        path: "settings/discord",
        element: (
          <ProtectedConfiguredRoute>
            <RetailToolDiscordConfig />
          </ProtectedConfiguredRoute>
        ),
      },
      {
        path: "account",
        element: (
          <ProtectedIsAuthenticatedRoute>
            <UserAccountConfig />
          </ProtectedIsAuthenticatedRoute>
        ),
      },

      // Legacy redirects — keep old URLs working. The :r segment is intentionally
      // discarded: region is now an account-level setting (userData.region), not a
      // URL param, so the destination tools read region from the user profile.
      { path: "retail-cross-realm-flipping-tool/:r", element: <Navigate to="/tools/flipping" replace /> },
      { path: "retail-cross-realm-flipping-tool", element: <Navigate to="/tools/flipping" replace /> },
      { path: "retail-cross-realm-housing-flipping-tool/:r", element: <Navigate to="/tools/housing" replace /> },
      { path: "retail-cross-realm-housing-flipping-tool", element: <Navigate to="/tools/housing" replace /> },
      { path: "retail-cross-realm-dropship-tool/:r", element: <Navigate to="/tools/dropship" replace /> },
      { path: "retail-cross-realm-dropship-tool", element: <Navigate to="/tools/dropship" replace /> },
      { path: "retail-inventory-analysis-tool/:r", element: <Navigate to="/tools/inventory" replace /> },
      { path: "retail-inventory-analysis-tool", element: <Navigate to="/tools/inventory" replace /> },
      { path: "retail-cross-realm-stats/:r", element: <Navigate to="/tools/stats" replace /> },
      { path: "retail-cross-realm-stats", element: <Navigate to="/tools/stats" replace /> },
      { path: "retail-tsm-list-generator/:r", element: <Navigate to="/tools/tsm" replace /> },
      { path: "retail-tsm-list-generator", element: <Navigate to="/tools/tsm" replace /> },
      { path: "tsm-profile-setup-guide", element: <Navigate to="/guides/tsm-profile" replace /> },
      { path: "snipe-alerts", element: <NavigatePreserveSearch to="/tools/snipe" /> },
      { path: "config", element: <Navigate to="/settings/realm" replace /> },
      { path: "config-personal", element: <Navigate to="/settings/personal" replace /> },
      { path: "config-discord", element: <Navigate to="/settings/discord" replace /> },
      { path: "user/account", element: <Navigate to="/account" replace /> },

      // Catch-all route
      { path: "*", element: <Home /> },
    ],
  },
]);

// Main App component
const App: React.FC = () => {
  return (
    <BackgroundWrapper>
      <RouterProvider router={router} />
      <ToastContainer limit={6} theme="dark" autoClose={4000} />
    </BackgroundWrapper>
  );
};

export default App;